import React, { useEffect, useState } from 'react';
import { Table } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { reportingOp } from 'store/ducks/Reportings';
import { RootState } from 'store/configureStore';
import { set_isAdditionalDisplayed } from 'store/ducks/Reportings/reportingSlice';
import { DataCertificatesICTSystem } from 'store/ducks/Reportings/types';
import { certificateColumnsICTSystemsFunctions } from 'components/Columns/CertificateForICTSystemsFunction';

interface reportDataProps {
  entityName: string;
}

const CertificatesByICTSystemsByICTServices: React.FC<reportDataProps> = ({
  entityName,
}) => {
  const {
    date,
    is_additional_displayed,
    ict_system_by_ict_service,
    ict_system_by_function,
  } = useSelector((state: RootState) => state.reportings);

  const [data, setData] = useState<DataCertificatesICTSystem[]>([]);
  const dispatch = useDispatch();

  const fetchData = async () => {
    dispatch(set_isAdditionalDisplayed(false));

    try {
      const ictSystemId = ict_system_by_ict_service || ict_system_by_function;

      if (!ictSystemId) {
        setData([]);
        return;
      }

      const [ictServiceResponse, functionResponse] = await Promise.all([
        ict_system_by_ict_service
          ? reportingOp.fetchCertificatesByICTSystemICTService({
              entity_name: entityName,
              ict_system_id: ict_system_by_ict_service,
              date,
            })
          : null,

        ict_system_by_function
          ? reportingOp.fetchCertificatesByICTSystemFunction({
              entity_name: entityName,
              ict_system_id: ict_system_by_function,
              date,
            })
          : null,
      ]);

      const ictServiceCertificates =
        ictServiceResponse && Array.isArray(ictServiceResponse.certificates)
          ? ictServiceResponse.certificates.map((item: any) => ({
              certificate_id: item.certificate_id,
              id: item.id,
              description: item.certificate_description,
              certificate_registry: item.certificate_registry,
              date_of_certificate_register: item.date_of_certificate_register,
              date_of_issuance: item.date_of_issuance,
              start_date: item.start_date,
              end_date: item.end_date,
              validity: item.validity,
              ict_system_name: item.ict_system_name,

              // ICT Service specific
              ict_service_description: item.ict_service_description,
              ict_service_id: item.ict_service_id,
              ict_service_name: item.ict_service_name,

              // Function should be empty
              function_id: undefined,
              function_name: undefined,
              contractual_arrangement_name: item.contractual_arrangement_name,
              name_of_entity: item.name_of_entity,
              third_party_provider_name: item.third_party_provider_name,
              third_party_service_provider_id:
                item.third_party_service_provider_id,
              certificate_source: 'ICT Service',
            }))
          : [];

      const functionCertificates =
        functionResponse && Array.isArray(functionResponse.certificates)
          ? functionResponse.certificates.map((item: any) => ({
              certificate_id: item.certificate_id,
              id: item.id,
              description: item.certificate_description,
              certificate_registry: item.certificate_registry,
              date_of_certificate_register: item.date_of_certificate_register,
              date_of_issuance: item.date_of_issuance,
              start_date: item.start_date,
              end_date: item.end_date,
              validity: item.validity,
              ict_system_name: item.ict_system_name,

              // Function specific
              function_id: item.function_id,
              function_name: item.function_name,

              // ICT Service should be empty
              ict_service_id: undefined,
              ict_service_name: undefined,
              ict_service_description: undefined,
              contractual_arrangement_name: item.contractual_arrangement_name,
              name_of_entity: item.name_of_entity,
              third_party_provider_name: item.third_party_provider_name,
              third_party_service_provider_id:
                item.third_party_service_provider_id,
              certificate_source: 'Function',
            }))
          : [];

      const allCertificates = [
        ...ictServiceCertificates,
        ...functionCertificates,
      ];

      const formattedData: DataCertificatesICTSystem[] = allCertificates.map(
        (dataItem: any, index: number) => ({
          key: `${dataItem.certificate_id ?? dataItem.id}-${index}`,
          id: dataItem.id ?? dataItem.certificate_id,
          description: dataItem.certificate_description ?? dataItem.description,
          certificate_id: dataItem.certificate_id,
          certificate_registry: dataItem.certificate_registry,
          contractual_arrangement_name: dataItem.contractual_arrangement_name,
          date_of_certificate_register: dataItem.date_of_certificate_register,
          date_of_issuance: dataItem.date_of_issuance,
          end_date: dataItem.end_date,
          function_id: dataItem.function_id,
          function_name: dataItem.function_name,
          ict_service_id: dataItem.ict_service_id,
          ict_service_name: dataItem.ict_service_name,
          ict_service_description: dataItem.ict_service_description,
          ict_system_name: dataItem.ict_system_name,
          name_of_entity: dataItem.name_of_entity,
          start_date: dataItem.start_date,
          third_party_provider_name: dataItem.third_party_provider_name,
          third_party_service_provider_id:
            dataItem.third_party_service_provider_id,
          validity: dataItem.validity,
          certificate_source: dataItem.certificate_source,
        }),
      );
      setData(formattedData);
    } catch (error) {
      console.log('Error fetching certificates:', error);
      setData([]);
    }
  };

  useEffect(() => {
    if (ict_system_by_ict_service || ict_system_by_function) {
      fetchData();
    }
  }, [
    is_additional_displayed,
    ict_system_by_ict_service,
    ict_system_by_function,
    date,
  ]);

  return (
    <Table
      columns={certificateColumnsICTSystemsFunctions}
      dataSource={data}
      scroll={{ x: 1000, y: 430 }}
    />
  );
};

export default CertificatesByICTSystemsByICTServices;
